import { CheckOutlineIcon, ErrorOutlineIcon } from '@ikyucom/design-system-icons-react'
import { RadioButton } from 'macaron-frontend-ui'
import React, { useCallback, useEffect, useRef } from 'react'
import type { MouseEvent } from 'react'
import Image from '~/components/image/Image'
import { StackableFusuma } from '~/components/modal/StackableFusuma'
import { StackableSlideUpModal } from '~/components/modal/StackableSlideUpModal'
import { useEnv } from '~/env'
import { useCard } from '~/features/reservationInput/store/stateMachine/context'
import { useCards } from '~/features/reservationInput/store/stateMachine/state'
import type { ReserveInputCard } from '~/features/reservationInput/store/stateMachine/state'
import {
  CC_AMEX_IMAGE_URL,
  CC_DINERS_IMAGE_URL,
  CC_DISCOVER_IMAGE_URL,
  CC_JCB_IMAGE_URL,
  CC_MASTER_IMAGE_URL,
  CC_VISA_IMAGE_URL,
} from '~/lib/constants/constants'
import { normalizeCardBrand } from '~/lib/reservationInput/card'
import type { CardBrand as CardBrandType } from '~/lib/reservationInput/card'
import { useIsPC } from '~/lib/ua'
import { useBack, useReserveOverlay } from '~/navigation'
import { css, sva } from '~/styled-system/css'
import { hstack } from '~/styled-system/patterns'
import { Button } from '~/ui'
import { Dialog } from '~/ui/Dialog'
import { useSelectedCardStatus, useSetSelectedCardStatus } from '../store/card'
import { PayjpNewCard } from './CancelGuard'
import { NewCardInput } from './CardPayment'
import {
  maskedNumberDisplay,
  useDeleteCardResult,
  useIsCancelGuardPlan,
  useSetDeletePayjpCard,
  useSetSubmitPayjpCard,
} from './cancel-guard'
import { useSubmitCardButtonDisabled, useSubmitted, useTokenError } from './card-fusuma'
import { useSetSubmit } from './card-payment'
import { scope } from './scope'

const CREDIT_CARD_LIMIT = 5

export function CardFusuma() {
  const [context, willBeUnmounted] = useReserveOverlay('NEW_RESERVE_INPUT_CREDIT_CARD')
  if (context === undefined) {
    return null
  }
  return (
    <scope.Provider>
      <StackableFusuma title="クレジットカード情報" willBeUnmounted={willBeUnmounted}>
        <Body />
      </StackableFusuma>
    </scope.Provider>
  )
}

export function CardModal() {
  const [context, willBeUnmounted] = useReserveOverlay('NEW_RESERVE_INPUT_CREDIT_CARD')
  if (context === undefined) {
    return null
  }
  return (
    <scope.Provider>
      <StackableSlideUpModal willBeUnmounted={willBeUnmounted}>
        <h3 className={css({ p: '6', pb: '0', fontWeight: 'bold', fontSize: '2xl' })}>
          クレジットカード情報
        </h3>
        <Body />
      </StackableSlideUpModal>
    </scope.Provider>
  )
}

const bodyStyles = sva({
  slots: ['container'],
  base: {
    container: {
      spaceY: '3',
      p: '3',
      pb: '16',
      _pc: {
        px: '6',
        pt: '5',
        pb: '12',
      },
    },
  },
})

function Body() {
  const isPC = useIsPC()
  const Submit = isPC ? PcSubmit : SdSubmit
  useBody()
  const classes = bodyStyles()
  return (
    <>
      <div className={classes.container}>
        <MyCards />
        <NewCard />
      </div>
      <Submit />
    </>
  )
}

function useBody() {
  const submitted = useSubmitted()
  const back = useBack()
  useEffect(() => {
    if (submitted === 'finished') {
      back()
    }
  }, [back, submitted])
}

const myCardsStyles = sva({
  slots: ['container'],
  base: {
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderRadius: 'default',
      border: 'medium',
      p: '3',
      fontSize: 'sm',
      _pc: {
        p: '4',
        fontSize: 'md',
      },
    },
  },
})

function MyCards() {
  const { cards, selected, onChange } = useMyCardsComponent()
  const isPC = useIsPC()
  const isCancelGuardPlan = useIsCancelGuardPlan()

  if (cards.length === 0 || selected === undefined) {
    return null
  }
  const classes = myCardsStyles()
  const Card = isPC ? MyCard : MyCardMultiline
  return (
    <>
      {isCancelGuardPlan && <CardDeletedMessage />}
      {cards.map((card) => (
        <div className={classes.container} key={card.cardId}>
          <RadioButton
            value={card.cardId}
            checked={selected.type === 'registered' && selected.cardId === card.cardId}
            onChange={onChange}
          >
            <Card card={card} />
          </RadioButton>
          {isCancelGuardPlan && <DeleteCard card={card} />}
        </div>
      ))}
    </>
  )
}

function DeleteCard({ card }: { card: ReserveInputCard }) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const setDeletePayjpCard = useSetDeletePayjpCard()
  const deleteCard = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      const cardId = e.currentTarget.dataset.cardId ?? ''
      await setDeletePayjpCard(cardId)
      closeModal()
    },
    [setDeletePayjpCard]
  )
  const showModal = useCallback(() => dialogRef.current?.showModal(), [])
  const closeModal = useCallback(() => dialogRef.current?.close(), [])

  return (
    <>
      <button type="button" onClick={showModal} className={css({ color: 'link', fontSize: 'sm' })}>
        削除
      </button>
      <Dialog ref={dialogRef}>
        <div
          className={css({
            display: 'flex',
            p: '5',
            flexDirection: 'column',
            gap: '5',
            fontSize: 'md',
            color: 'gray.800',
          })}
        >
          <p className={css({ fontSize: 'lg' })}>クレジットカード情報を削除しますか？</p>
          <MyCard card={card} />
          <div
            className={css({
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '2',
            })}
          >
            <Button
              onClick={closeModal}
              css={{
                border: 'none',
                color: 'gray.700',
                transition: '[background-color 0.2s]',
                _hover: { bg: 'gray.200' },
              }}
              size="xs"
            >
              キャンセル
            </Button>
            <Button color="tertiary" data-card-id={card.cardId} onClick={deleteCard}>
              削除
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  )
}

function CardDeletedMessage() {
  const deleteCardResult = useDeleteCardResult()
  if (deleteCardResult?.type === 'error') {
    return (
      <div
        className={css({
          display: 'flex',
          alignItems: 'center',
          columnGap: '2',
          border: 'light',
          borderColor: 'error.border',
          p: '2',
          fontSize: 'sm',
          color: 'error.text',
        })}
      >
        <ErrorOutlineIcon width={14} height={14} className={css({ flexShrink: '0' })} />
        {deleteCardResult.message}
      </div>
    )
  }
  if (deleteCardResult?.type === 'completed') {
    return (
      <div
        className={css({
          display: 'flex',
          alignItems: 'center',
          columnGap: '1',
          fontSize: 'sm',
        })}
      >
        <CheckOutlineIcon
          width={16}
          height={16}
          className={css({ color: 'success', flexShrink: '0' })}
        />
        カード情報を削除しました
      </div>
    )
  }
  return null
}

function useMyCardsComponent() {
  const cards = useCards()
  const selected = useSelectedCardStatus()
  const set = useSetSelectedCardStatus()
  const init = useCard()
  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      set({ type: 'registered', cardId: e.target.value })
    },
    [set]
  )
  useEffect(() => {
    if (init === undefined || init.type === 'new') {
      set({ type: 'new' })
    } else {
      set({ type: 'registered', cardId: init.cardId })
    }
  }, [init, set])
  return { cards, selected, onChange }
}

const myCardStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: { base: '[32px]', _pc: '[36px]' },
  height: { base: '[18px]', _pc: '[20px]' },
})

function MyCard({ card }: { card: ReserveInputCard }) {
  return (
    <div className={css({ display: 'flex', alignItems: 'center' })}>
      <span className={myCardStyle}>
        <CardBrand brand={normalizeCardBrand(card.brand)} />
      </span>
      <span>
        {maskedNumberDisplay(card.maskedNumber)} {card.holderName}（{card.goodThru}）
      </span>
    </div>
  )
}

function MyCardMultiline({ card }: { card: ReserveInputCard }) {
  return (
    <div>
      <div className={css({ display: 'flex', alignItems: 'center' })}>
        <span className={myCardStyle}>
          <CardBrand brand={normalizeCardBrand(card.brand)} />
        </span>
        <span>{maskedNumberDisplay(card.maskedNumber)}</span>
      </div>
      {card.holderName}（{card.goodThru}）
    </div>
  )
}

export function CardBrand({ brand }: { brand?: CardBrandType }) {
  const className = css({
    width: 'auto',
    height: { base: '[18px]', _pc: '[20px]' },
    objectFit: 'contain',
  })
  switch (brand) {
    case 'VISA':
      return <Image src={CC_VISA_IMAGE_URL} className={className} alt="Visa" />
    case 'MASTER':
      return <Image src={CC_MASTER_IMAGE_URL} className={className} alt="MasterCard" />
    case 'DINERS':
      return <Image src={CC_DINERS_IMAGE_URL} className={className} alt="DinersClub" />
    case 'AMEX':
      return <Image src={CC_AMEX_IMAGE_URL} className={className} alt="AmericanExpress" />
    case 'JCB':
      return <Image src={CC_JCB_IMAGE_URL} className={className} alt="JCB" />
    case 'DISCOVER':
      return <Image src={CC_DISCOVER_IMAGE_URL} className={className} alt="Discover" />
    default:
      return null
  }
}

const newCardStyles = sva({
  slots: ['container', 'heading', 'error', 'text'],
  base: {
    container: {
      spaceY: { base: '3', _pc: '5' },
      borderRadius: 'default',
      border: 'medium',
      p: '3',
      _pc: {
        p: '4',
        fontSize: 'md',
      },
    },
    error: {
      _pc: {
        fontSize: 'sm',
      },
    },
    text: {
      color: 'error.text',
    },
  },
})

function NewCard() {
  const { env, selected, onChange, disabled, deletable, isCancelGuardPlan, visibleNewCard } =
    useMyCard()
  const classes = newCardStyles()
  const isPC = useIsPC()
  if (!visibleNewCard) {
    return null
  }
  return (
    <div className={classes.container}>
      <RadioButton checked={selected?.type === 'new'} disabled={disabled} onChange={onChange}>
        新しいカードを使用する
      </RadioButton>
      {disabled && (
        <div className={classes.error}>
          <p className={classes.text}>
            カード登録枚数が上限に達してます。
            {!isPC && <br />}
            {deletable && '会員サポート画面よりカード情報の削除をお願いします。'}
          </p>
          {deletable && (
            <a
              href={`${env.MEMBER_ORIGIN}/member/cards/`}
              className={css({ display: 'inline-block', color: 'link', mt: '1' })}
              target="_blank"
              rel="noreferrer"
            >
              削除はこちら
            </a>
          )}
        </div>
      )}
      {isCancelGuardPlan ? <PayjpNewCard /> : <NewCardInput />}
    </div>
  )
}

function useMyCard() {
  const myCards = useCards()
  const env = useEnv()
  const selected = useSelectedCardStatus()
  const set = useSetSelectedCardStatus()
  const onChange = useCallback(() => {
    set({ type: 'new' })
  }, [set])
  const isCancelGuardPlan = useIsCancelGuardPlan()

  const disabled = myCards.length >= CREDIT_CARD_LIMIT
  // GMOカード(事前カード決済プラン)は会員サポート画面から削除可能だが、PAY.JPカード(キャンセルガードプラン)は現在削除導線がない
  // TODO: カードふすま上に削除機能を設置する https://ikyu.slack.com/archives/C4JKVQYMT/p1732093008107879
  const deletable = !isCancelGuardPlan
  // カードふすま上ですべてのカードが削除された場合は、新しいカードの入力フォームを非表示にしたい
  // カードふすま上と予約入力上に、同時にPAY.JP 入力フォーム要素が存在すると、
  // PAY.JP トークン生成が正常に動作しないため
  const visibleNewCard = !isCancelGuardPlan || myCards.length > 0
  return { env, selected, onChange, disabled, deletable, isCancelGuardPlan, visibleNewCard }
}

const sdSubmitStyles = sva({
  slots: ['container', 'error'],
  base: {
    container: {
      position: 'fixed',
      bottom: '0',
      width: 'full',
      bg: 'white',
      px: '3',
      pb: '3',
    },
    error: {
      marginTop: '1',
      fontSize: 'xs',
      color: 'error.text',
    },
  },
})

function SdSubmit() {
  const { disabled, error, submit } = useSubmit()
  const classes = sdSubmitStyles()
  return (
    <div className={classes.container}>
      {error !== undefined && <p className={classes.error}>{error}</p>}
      <Button color="secondary" disabled={disabled} onClick={submit} spread={true}>
        決定する
      </Button>
    </div>
  )
}

const pcSubmitStyles = sva({
  slots: ['container', 'error', 'text', 'button', 'cancel'],
  base: {
    container: {
      position: 'sticky',
      left: '0',
      bottom: '0',
      bg: 'white',
      shadow: 'outline.lg',
      p: '6',
    },
    text: {
      pointerEvents: 'none',
      width: 'full',
      bg: 'white',
      pb: '2',
      textAlign: 'left',
      fontSize: 'sm',
      color: 'error.text',
    },
    button: {
      spaceY: '5',
      spaceX: '4',
      px: '6',
      pt: '4',
      pb: '10',
    },
    cancel: {
      color: 'gray.700',
      py: '3',
      px: '2',
      rounded: 'md',
      transition: '[all 0.2s]',
      _hover: {
        bg: 'gray.200',
      },
    },
  },
})

function PcSubmit() {
  const { disabled, error, submit } = useSubmit()
  const back = useBack()
  const onCancel = useCallback(() => {
    back('NEW_RESERVE_INPUT')
  }, [back])
  const classes = pcSubmitStyles()
  return (
    <div className={classes.container}>
      {error !== undefined && <p className={css({ color: 'error.text' })}>{error}</p>}
      <div className={hstack({ justify: 'flex-end', gap: '4' })}>
        <button type="button" onClick={onCancel} className={classes.cancel}>
          キャンセル
        </button>
        <Button color="secondary" disabled={disabled} onClick={submit} size="lg">
          決定する
        </Button>
      </div>
    </div>
  )
}

export function useSubmit() {
  const isCancelGuardPlan = useIsCancelGuardPlan()
  const disabled = useSubmitCardButtonDisabled()
  const error = useTokenError()
  const setSubmit = useSetSubmit()
  const setSubmitPayjpCard = useSetSubmitPayjpCard()
  const submit = useCallback(async () => {
    if (isCancelGuardPlan) {
      await setSubmitPayjpCard()
    } else {
      await setSubmit()
    }
  }, [setSubmit, setSubmitPayjpCard, isCancelGuardPlan])

  return { disabled, error, submit }
}
